import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Firestore,
} from 'firebase/firestore'
import type { WorkspaceRole } from '../context/WorkspaceContext'
import { firebaseFunctions, firestoreDb } from './firebase'
import { httpsCallable } from 'firebase/functions'
import { callable } from './callable'
import { approveJoinRequestCallable } from './memberOpsClient'
import { createEmailInvite, createPhoneInvite } from './inviteService'

export type MembershipStatus = 'PENDING' | 'ACTIVE' | 'DECLINED' | 'REVOKED'

export type WorkspaceMemberRecord = {
  workspaceId: string
  workspaceName: string
  email: string
  user_email?: string
  uid: string
  userId?: string
  role: WorkspaceRole
  status: MembershipStatus
  invitedBy: string
  invitedAt: unknown
  credits?: number
}

export const normalizeMemberEmail = (email: string | null | undefined) => email?.trim().toLowerCase() ?? ''

export const getEmailMembershipId = (workspaceId: string, email: string) =>
  `${workspaceId}_${normalizeMemberEmail(email)}`

export const getLegacyUidMembershipId = (workspaceId: string, uid: string) => `${workspaceId}_${uid}`

export const getMembershipRef = (db: Firestore, workspaceId: string, email: string) =>
  doc(db, 'workspace_members', getEmailMembershipId(workspaceId, email))

export const createActiveOwnerMembership = async (
  db: Firestore,
  input: {
    workspaceId: string
    workspaceName: string
    workspaceSlug?: string
    uid: string
    email: string
    displayName: string
  },
) => {
  const email = normalizeMemberEmail(input.email)

  const timestamp = serverTimestamp()
  const membership = {
    workspaceId: input.workspaceId,
    workspaceName: input.workspaceName,
    workspaceSlug: input.workspaceSlug ?? null,
    email,
    user_email: email,
    uid: input.uid,
    userId: input.uid,
    role: 'OWNER' as const,
    status: 'ACTIVE' as const,
    credits: 0,
    invitedBy: input.displayName || email,
    invitedAt: timestamp,
    joinedAt: timestamp,
    respondedAt: timestamp,
    createdAt: timestamp,
    inviteStatusHistory: [{ status: 'ACTIVE', at: new Date().toISOString(), by: input.displayName || email }],
  }

  await Promise.all([
    setDoc(getMembershipRef(db, input.workspaceId, email), membership),
    setDoc(doc(db, 'users', input.uid, 'workspaceMemberships', input.workspaceId), {
      uid: input.uid,
      workspaceId: input.workspaceId,
      role: 'OWNER',
      status: 'ACTIVE',
      boardRoles: null,
      credits: 0,
      defaultDashboardView: null,
      joinedAt: timestamp,
      updatedAt: timestamp,
    }, { merge: true }),
  ])
}

/** Normalize a phone string to E.164 (e.g., "+919876543210"). Throws on invalid input. */
export const normalizePhoneNumberE164 = (regionCode: string, localNumber: string): string => {
  const region = regionCode.trim()
  if (!/^\+\d{1,4}$/.test(region)) throw new Error('Invalid region code.')
  const digits = localNumber.replace(/\D/g, '')
  if (digits.length < 6 || digits.length > 12) throw new Error('Phone number must be 6–12 digits.')
  return `${region}${digits}`
}

/** Membership document id for phone-keyed pending invites. */
export const phoneMembershipDocId = (workspaceId: string, e164: string) =>
  `${workspaceId}_phone:${e164}`

/**
 * @deprecated Deprecated in Phase 2. Writes to pending_invites via createPhoneInvite.
 */
export const createPendingPhoneMembershipInvite = async (
  _db: Firestore,
  input: {
    workspaceId: string
    workspaceName: string
    workspaceSlug?: string
    phoneE164: string
    role: WorkspaceRole
    invitedBy: string
  },
) => {
  return createPhoneInvite({
    workspaceId: input.workspaceId,
    workspaceName: input.workspaceName,
    workspaceSlug: input.workspaceSlug,
    role: input.role,
    invitedBy: input.invitedBy,
    phoneE164: input.phoneE164,
  })
}

/**
 * @deprecated Deprecated in Phase 2. Writes to pending_invites via createEmailInvite.
 */
export const createPendingMembershipInvite = async (
  _db: Firestore,
  input: {
    workspaceId: string
    workspaceName: string
    workspaceSlug?: string
    email: string
    role: WorkspaceRole
    invitedBy: string
  },
) => {
  return createEmailInvite({
    workspaceId: input.workspaceId,
    workspaceName: input.workspaceName,
    workspaceSlug: input.workspaceSlug,
    role: input.role,
    invitedBy: input.invitedBy,
    email: input.email,
  })
}


export const revokePendingMembershipInvite = async (
  db: Firestore,
  membershipId: string,
  revokedBy: string,
) => {
  await updateDoc(doc(db, 'workspace_members', membershipId), {
    status: 'REVOKED',
    revokedAt: serverTimestamp(),
    inviteStatusHistory: [{ status: 'REVOKED', at: new Date().toISOString(), by: revokedBy }],
  })
}

export const approveJoinRequest = async (
  _db: Firestore,
  input: {
    requestId: string
    workspaceId: string
    workspaceName?: string
    applicantUid?: string
    applicantEmail?: string
    approvedBy?: string
    role?: string
  },
) => {
  return approveJoinRequestCallable({
    workspaceId: input.workspaceId,
    requestId: input.requestId,
    role: input.role ?? 'EDITOR',
  })
}

export const rejectJoinRequest = async (db: Firestore, requestId: string) => {
  await setDoc(doc(db, 'join_requests', requestId), {
    status: 'REJECTED',
    rejectedAt: serverTimestamp(),
  }, { merge: true })
}

export const approveOrRejectRequest = async (
  requestId: string,
  status: 'APPROVED' | 'REJECTED',
) => {
  if (status === 'REJECTED') {
    await setDoc(doc(firestoreDb, 'join_requests', requestId), {
      status: 'REJECTED',
      rejectedAt: serverTimestamp(),
    }, { merge: true })
    return
  }

  const requestSnapshot = await getDoc(doc(firestoreDb, 'join_requests', requestId))
  if (!requestSnapshot.exists() || requestSnapshot.data()?.status !== 'PENDING') {
    throw new Error('This join request is no longer pending.')
  }
  const workspaceId = typeof requestSnapshot.data()?.workspaceId === 'string' ? requestSnapshot.data()?.workspaceId : ''
  if (!workspaceId) {
    throw new Error('This join request is missing account details.')
  }

  await approveJoinRequestCallable({
    workspaceId,
    requestId,
    role: 'EDITOR',
  })
}

export const migrateLegacyMembershipIfNeeded = async (
  db: Firestore,
  input: {
    workspaceId: string
    uid: string
    email: string
  },
) => {
  const email = normalizeMemberEmail(input.email)
  if (!email) return

  const emailRef = getMembershipRef(db, input.workspaceId, email)
  const emailSnapshot = await getDoc(emailRef)
  if (emailSnapshot.exists()) return

  const legacyRef = doc(db, 'workspace_members', getLegacyUidMembershipId(input.workspaceId, input.uid))
  const legacySnapshot = await getDoc(legacyRef)
  if (!legacySnapshot.exists()) return

  const legacyData = legacySnapshot.data() as Partial<WorkspaceMemberRecord>
  if (legacyData.status && legacyData.status !== 'ACTIVE') return

  await runTransaction(db, async (transaction) => {
    const freshEmailSnapshot = await transaction.get(emailRef)
    const freshLegacySnapshot = await transaction.get(legacyRef)
    if (freshEmailSnapshot.exists() || !freshLegacySnapshot.exists()) return

    transaction.set(emailRef, {
      ...freshLegacySnapshot.data(),
      workspaceId: input.workspaceId,
      email,
      user_email: email,
      uid: input.uid,
      userId: input.uid,
      status: 'ACTIVE',
    }, { merge: true })
    transaction.delete(legacyRef)
  })
}

const endWorkspaceMemberCallable = httpsCallable<
  { workspaceId: string; targetUserId: string; reason?: string },
  { revoked: boolean; unassignedTaskCount: number }
>(firebaseFunctions, 'endWorkspaceMember')

export const endWorkspaceMember = async (input: {
  workspaceId: string
  targetUserId: string
  reason?: string
}) => (await endWorkspaceMemberCallable(input)).data

export const leaveWorkspace = async (input: {
  workspaceId: string
  reason?: string
}) =>
  callable<{ workspaceId: string; reason?: string }, { revoked: boolean; unassignedTaskCount: number }>(
    'leaveWorkspace',
    input,
  )

export const leaveWorkspaceAndPurgeOutbox = async (input: {
  workspaceId: string
  reason?: string
}): Promise<{ revoked: boolean; unassignedTaskCount: number }> => {
  const result = await leaveWorkspace(input)
  try {
    const { clearHelperOutboxForWorkspace } = await import('./helper/helperOutboxDb')
    await clearHelperOutboxForWorkspace(input.workspaceId)
  } catch {
    // ignore
  }
  try {
    const { clearMemberOutboxForWorkspace } = await import('./memberOutboxService')
    await clearMemberOutboxForWorkspace(input.workspaceId)
  } catch {
    // ignore
  }
  return result
}

export const addWorkspaceOwner = async (input: {
  workspaceId: string
  targetUserId: string
}) =>
  callable<{ workspaceId: string; targetUserId: string }, { ok: boolean; alreadyOwner?: boolean }>(
    'addWorkspaceOwner',
    input,
  )

export const removeWorkspaceOwner = async (input: {
  workspaceId: string
  targetUserId: string
}) =>
  callable<{ workspaceId: string; targetUserId: string }, { ok: boolean }>(
    'removeWorkspaceOwner',
    input,
  )

export const transferWorkspace = async (input: {
  workspaceId: string
  newUid: string
}) =>
  callable<{ workspaceId: string; newUid: string }, { ok: boolean }>(
    'transferWorkspace',
    input,
  )
